//@ts-check
import {Button} from '@/components-v2/atoms/Button/Button'
import {useFeatureFlagLanguages} from '@/hooks/useFeatureFlagLanguages'
import {useMemo} from 'react'
import {useProductReview} from '../../../context/ProductReviewContext'
import {ProductReviewField} from '../../ui/ProductReviewField'
import {getDuplicateSubCategoryCodes} from '../../utils/productReviewValidation'
import {selectCountryCode} from '@/store/slices/countryCodeSlice'
import {useSelector} from 'react-redux'
import {COUNTRY_CODES} from '@/utils/constants/countryConstants'

const MAX_SUB_CATEGORIES = 10
const EMPTY_SUB_CATEGORY = {code: '', values: {}, url: ''}
const DUPLICATE_CODE_ERROR = 'Sub Category with the same ID is already added for this product'

/**
 * Renders the Sub Category toggle and, when enabled, the list of subcategories (code + one
 * value per language), with add/remove controls and duplicate-code validation.
 * Reads and writes product data through the Product Review context (no props).
 */
export const SubCategorySection = () => {
  const {isEditMode, productData, updateEditableField} = useProductReview()
  const languages = useFeatureFlagLanguages()

  const subCategoryFlag = Boolean(productData?.category?.subCategoryFlag)
  const subCategories = useMemo(
    () => (Array.isArray(productData?.category?.subCategories) ? productData.category.subCategories : []),
    [productData]
  )
  const duplicateCodeErrors = useMemo(() => getDuplicateSubCategoryCodes(subCategories), [subCategories])
  const isAddDisabled = subCategories.length >= MAX_SUB_CATEGORIES

  const handleToggleChange = checked => {
    updateEditableField('category', 'subCategoryFlag', checked)
    updateEditableField('category', 'subCategories', checked ? [{...EMPTY_SUB_CATEGORY}] : [])
  }

  const handleCodeChange = (index, value) => {
    const nextSubCategories = subCategories.map((item, i) => (i === index ? {...item, code: value} : item))
    updateEditableField('category', 'subCategories', nextSubCategories)
  }

  const handleValueChange = (index, languageCode, value) => {
    const nextSubCategories = subCategories.map((item, i) =>
      i === index ? {...item, values: {...(item.values || {}), [languageCode]: value}} : item
    )
    updateEditableField('category', 'subCategories', nextSubCategories)
  }

  const handleAddSubCategory = () => {
    if (isAddDisabled) return
    updateEditableField('category', 'subCategories', [...subCategories, {...EMPTY_SUB_CATEGORY}])
  }

  const handleRemoveSubCategory = index => {
    updateEditableField(
      'category',
      'subCategories',
      subCategories.filter((_, i) => i !== index)
    )
  }

  const countryCode = useSelector(selectCountryCode)
  const subcategoryRequired = countryCode === COUNTRY_CODES.US ? false : true

  return (
    <div className="flex flex-col divide-y divide-[var(--secondary-800)]">
      <ProductReviewField
        label="Sub Category"
        labelKey="subCategoryFlag"
        editable
        required={false}
        renderer="toggle"
        value={subCategoryFlag}
        onChange={handleToggleChange}
      />

      {subCategoryFlag &&
        subCategories.map((subCategory, index) => (
          <div key={`sub-category-${index}`} className="flex flex-col gap-1 py-2">
            <ProductReviewField
              label="Sub Category ID"
              labelKey={`subCategories-${index}-code`}
              editable
              required={subcategoryRequired}
              renderer="text"
              value={subCategory?.code}
              error={duplicateCodeErrors[index] ? DUPLICATE_CODE_ERROR : undefined}
              onChange={value => handleCodeChange(index, value)}
            />

            {languages.map(language => (
              <ProductReviewField
                key={`${index}-${language.code}`}
                label={language.language}
                labelKey={`subCategories-${index}-${language.code}`}
                editable
                required={subcategoryRequired}
                renderer="text"
                value={subCategory?.values?.[language.code]}
                onChange={value => handleValueChange(index, language.code, value)}
              />
            ))}

            {isEditMode && index !== 0 && index === subCategories.length - 1 && (
              <div className="flex justify-end">
                <Button
                  text="Remove Sub Category"
                  size="small"
                  variant="tertiary"
                  iconLeading="delete"
                  onClick={() => handleRemoveSubCategory(index)}
                />
              </div>
            )}
          </div>
        ))}

      {isEditMode && subCategoryFlag && (
        <div className="flex justify-start py-2">
          <Button
            text="Add Sub Category"
            size="small"
            variant="tertiary"
            iconLeading="add_box"
            disabled={isAddDisabled}
            onClick={handleAddSubCategory}
          />
        </div>
      )}
    </div>
  )
}
